import { useSelector } from "react-redux";
import { RootState } from "../state/store";

type HostApp = 'encompass' | 'tpoconnect' | 'standalone';

interface Host {
    /** The host application type */
    app: HostApp;
    /** True if running in TPO Connect */
    isTpoConnect: boolean;
    /** True if running in Encompass */
    isEncompass: boolean;
    /** True if TPO is enabled in configuration */
    isTPOEnabled: boolean;
    /** True if both in TPO Connect and TPO is enabled - TPO is usable */
    isTpoConnectUsable: boolean;
}

interface HostDetails {
    /** Host application details */
    host: Host;
}

/**
 * Custom hook to detect and provide details about the host application environment
 * @returns {HostDetails} Object containing host type and related boolean flags
 */
export const useHostDetails = (): HostDetails => {
    const isTPOConnectTraffic = useSelector((state: RootState) => state.originSlice.isTPOConnectTraffic);
    const configPlugInData = useSelector((state: RootState) => state.configPlugInData.data);
    const isTPOEnabled = configPlugInData?.Configuration?.EnableTpo ?? false;
    
    // Determine host type based on available indicators
    const getHostApp = (): HostApp => {
        if (isTPOConnectTraffic) {
            return 'tpoconnect';
        }
        
        // If we don't detect TPO Connect traffic, default to Encompass as the host application
        return 'encompass';
    };

    const hostApp = getHostApp();
    const isTpoConnect = hostApp === 'tpoconnect';
    
    return {
        host: {
            app: hostApp,
            isTpoConnect,
            isEncompass: hostApp === 'encompass',
            isTPOEnabled,
            isTpoConnectUsable: isTpoConnect && isTPOEnabled,
        },
    };
};